import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Team Seasons list + create page.
 *
 * Two things live here:
 *   1) A list of every season the caller is on (as owner OR rostered player)
 *   2) A "New season" panel that expands to collect a name, start/end
 *      dates, and each team's email roster.
 *
 * Standings/details for a specific season live on `/app/seasons/:id`.
 */
export default function TeamSeasons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    team_a_name: "Team A",
    team_b_name: "Team B",
    team_a_emails: "",
    team_b_emails: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 90 * 86400_000).toISOString().slice(0, 10),
  });
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team-seasons");
      setRows(data.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load seasons.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        team_a_name: form.team_a_name.trim() || "Team A",
        team_b_name: form.team_b_name.trim() || "Team B",
        team_a_emails: form.team_a_emails.split(/[\s,]+/).filter(Boolean),
        team_b_emails: form.team_b_emails.split(/[\s,]+/).filter(Boolean),
        start_date: form.start_date,
        end_date: form.end_date,
      };
      const { data } = await api.post("/team-seasons", payload);
      nav(`/app/seasons/${data.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't create season.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-24 max-w-3xl mx-auto" data-testid="seasons-page">
      <div className="px-5 pt-6">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="font-heading font-black text-3xl tracking-tighter-2 text-[var(--primary)]">Seasons</h1>
          <Link to="/app/leaderboard" className="text-xs font-bold text-[var(--primary)] hover:underline">
            Club Board →
          </Link>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          Ryder-Cup-style team battles across a whole season. Every fourball or match your teams play in the date range auto-updates the standings.
        </p>

        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          data-testid="seasons-toggle-new"
          className="w-full rounded-2xl py-2.5 px-4 font-heading font-black text-sm bg-[var(--primary)] text-white active:scale-[0.98] transition-transform"
        >
          {showForm ? "Cancel" : "+ New season"}
        </button>

        {showForm && (
          <form onSubmit={submit} className="mt-4 surface-card p-4 space-y-3" data-testid="seasons-form">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Season name (e.g. Summer '26)"
              data-testid="seasons-name"
              className="input-field w-full"
            />
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase font-bold text-[var(--text-secondary)]">
                Starts
                <input
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="seasons-start"
                  className="input-field w-full mt-1"
                />
              </label>
              <label className="text-[10px] uppercase font-bold text-[var(--text-secondary)]">
                Ends
                <input
                  type="date"
                  required
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="seasons-end"
                  className="input-field w-full mt-1"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase font-bold text-[var(--text-secondary)]">
                Team A name
                <input
                  type="text"
                  value={form.team_a_name}
                  onChange={(e) => setForm({ ...form, team_a_name: e.target.value })}
                  className="input-field w-full mt-1"
                />
              </label>
              <label className="text-[10px] uppercase font-bold text-[var(--text-secondary)]">
                Team B name
                <input
                  type="text"
                  value={form.team_b_name}
                  onChange={(e) => setForm({ ...form, team_b_name: e.target.value })}
                  className="input-field w-full mt-1"
                />
              </label>
            </div>
            <label className="block text-[10px] uppercase font-bold text-[var(--text-secondary)]">
              Team A emails (comma or space separated) — you&apos;re on Team A by default
              <textarea
                value={form.team_a_emails}
                onChange={(e) => setForm({ ...form, team_a_emails: e.target.value })}
                rows={2}
                placeholder="stu@example.com alex@example.com"
                data-testid="seasons-team-a-emails"
                className="input-field w-full mt-1 font-mono-num text-xs"
              />
            </label>
            <label className="block text-[10px] uppercase font-bold text-[var(--text-secondary)]">
              Team B emails
              <textarea
                required
                value={form.team_b_emails}
                onChange={(e) => setForm({ ...form, team_b_emails: e.target.value })}
                rows={2}
                placeholder="rob@example.com jamie@example.com"
                data-testid="seasons-team-b-emails"
                className="input-field w-full mt-1 font-mono-num text-xs"
              />
            </label>
            <p className="text-[10px] text-[var(--text-secondary)] italic">
              Players need a Fairway account. Unknown emails are ignored — invite them separately.
            </p>
            {error && <div className="text-sm text-[var(--accent-terracotta)]" data-testid="seasons-form-error">{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="seasons-submit"
              className="w-full rounded-2xl py-2.5 px-4 font-heading font-black text-sm bg-[var(--accent-turf)] text-[var(--primary)]"
            >
              {submitting ? "Creating…" : "Start season"}
            </button>
          </form>
        )}

        <div className="mt-6 space-y-2" data-testid="seasons-list">
          {loading && <div className="text-center text-sm italic text-[var(--text-secondary)] py-6">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-center text-sm italic text-[var(--text-secondary)] py-6" data-testid="seasons-empty">
              No seasons yet. Kick off your first Ryder-Cup-style battle above.
            </div>
          )}
          {rows.map((s) => (
            <Link
              key={s.id}
              to={`/app/seasons/${s.id}`}
              className="block surface-card p-3 hover:opacity-90 transition-opacity"
              data-testid={`season-row-${s.id}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-heading font-bold text-sm truncate">{s.name}</span>
                <span className="text-[10px] text-[var(--text-secondary)] font-mono-num">
                  {s.start_date} → {s.end_date}
                </span>
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                <span className="font-bold text-[var(--primary)]">{s.team_a_name}</span>
                {" vs "}
                <span className="font-bold text-[var(--accent-terracotta)]">{s.team_b_name}</span>
                {" · "}
                {(s.team_a_user_ids?.length || 0) + (s.team_b_user_ids?.length || 0)} players
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
